import { createContext, FC, ReactElement, useContext, useEffect, useState } from 'react';
import { addTypeToStorageRequest } from 'services/storageRequestService';
import { getNotificationsEventSource } from 'services/userService';
import { addStorageRequestAction } from 'state/actions/storageRequest';
import { StorageRequest } from 'types/storageRequest';
import { useChatContext } from '../ChatContext/ChatContext';
import { useStorageRequestContext } from '../storageRequestContext/storageRequest.context';
import { useUserContext } from '../userContext/userContext';

type Message = {
  data: string;
  lastEventId: string;
  origin: string;
  type: string;
};

type NotificationContext = {
  hasNotification: boolean;
};
type UseNotificationContext = () => NotificationContext;

const NotificationContext = createContext({} as NotificationContext);

export const NotificationProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const {
    user: { isStorageProvider, userData },
  } = useUserContext();
  const {
    storageRequestsState: { storageRequests },
    dispatch,
  } = useStorageRequestContext();
  const { hasUnreadMessages } = useChatContext();
  const [isNotificationsInit, setIsNotificationsInit] = useState(true);

  useEffect(() => {
    if (userData && isNotificationsInit) {
      setIsNotificationsInit(false);
      const notifications = getNotificationsEventSource();

      notifications.addEventListener('message', (message: Message) => {
        const incomingStorageRequest = JSON.parse(message.data) as StorageRequest;
        const hasStorageRequestAlreadyBeenAdded = storageRequests.some(
          (stateStorageRequest) => stateStorageRequest.id === incomingStorageRequest.id,
        );
        if (!hasStorageRequestAlreadyBeenAdded) {
          addStorageRequestAction(
            dispatch,
            addTypeToStorageRequest(incomingStorageRequest, 'received'),
          );
        }
      });
    }
  }, [dispatch, isNotificationsInit, storageRequests, userData]);

  const unseenStorageRequests = storageRequests.some(
    (storageRequest) => !storageRequest.isSeen && storageRequest.type === 'received',
  );

  const hasNotification = isStorageProvider
    ? unseenStorageRequests || hasUnreadMessages
    : hasUnreadMessages;

  return (
    <NotificationContext.Provider value={{ hasNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext: UseNotificationContext = () =>
  useContext(NotificationContext);
